"use client";

import { useId, useRef, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { cn } from "@/lib/cn";
import { FieldFrame, fieldStateClasses, fieldSurface } from "@/components/ui/field";
import { filterCountries } from "@/lib/countries";
import { countryName } from "@/lib/media";

interface CountryPickerProps {
  label?: React.ReactNode;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  /** 2-letter country code, or "" when no country is chosen. */
  value: string;
  onChange: (code: string) => void;
  containerClassName?: string;
}

/**
 * Type-to-filter country autocomplete over the client-side `COUNTRIES` list
 * (same combobox interaction as `CityPicker`/`LocationPicker`). Free text is
 * never a value — closing without picking snaps the field back to the current
 * selection. Focusing with an empty field lists every country. Keyboard: ↑/↓
 * to move, Enter to pick, Esc to close. The × button clears the selection.
 */
export function CountryPicker({
  label,
  hint,
  error,
  value,
  onChange,
  containerClassName,
}: CountryPickerProps) {
  const t = useTranslations("countryPicker");
  const locale = useLocale();
  const inputId = useId();
  const listId = `${inputId}-list`;
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedLabel = value ? countryName(value, locale) : "";
  const [text, setText] = useState(selectedLabel);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  // Snap the field to the selected label whenever the value changes (pick,
  // clear, parent reset) — render-time state reset, no effect needed.
  const [prevLabel, setPrevLabel] = useState(selectedLabel);
  if (selectedLabel !== prevLabel) {
    setPrevLabel(selectedLabel);
    setText(selectedLabel);
  }

  const hasError = Boolean(error);
  const suggestions = filterCountries(text, locale);

  function choose(code: string) {
    onChange(code);
    setText(countryName(code, locale));
    setOpen(false);
    setActiveIndex(-1);
  }

  function clear() {
    onChange("");
    setText("");
    setActiveIndex(-1);
    inputRef.current?.focus();
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActiveIndex((i) => Math.min(suggestions.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      if (open && activeIndex >= 0 && suggestions[activeIndex]) {
        e.preventDefault();
        choose(suggestions[activeIndex].code);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
      setText(selectedLabel);
    }
  }

  const showMenu = open && suggestions.length > 0;

  return (
    <FieldFrame
      label={label}
      hint={hint}
      error={error}
      errorId={`${inputId}-error`}
      htmlFor={inputId}
      className={containerClassName}
    >
      <div className="relative">
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          role="combobox"
          aria-expanded={showMenu}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={
            activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined
          }
          aria-invalid={hasError || undefined}
          aria-describedby={hasError ? `${inputId}-error` : undefined}
          autoComplete="off"
          spellCheck={false}
          placeholder={t("placeholder")}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setOpen(true);
            setActiveIndex(-1);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() =>
            setTimeout(() => {
              // Focus can return before the timeout fires (the × button
              // refocuses the input) — restoring then would resurrect a stale
              // label over the just-cleared field.
              if (document.activeElement === inputRef.current) return;
              setOpen(false);
              setText(selectedLabel);
            }, 120)
          }
          onKeyDown={onKeyDown}
          className={cn(
            fieldSurface,
            fieldStateClasses(hasError),
            "h-11",
            text && "pr-10",
          )}
        />

        {text && (
          <button
            type="button"
            onClick={clear}
            aria-label={t("clearCountry")}
            className="absolute inset-y-0 right-2.5 flex items-center text-white/40 transition-colors hover:text-white/80"
          >
            <svg viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="m6 6 12 12M18 6 6 18" />
            </svg>
          </button>
        )}

        {showMenu && (
          <ul
            id={listId}
            role="listbox"
            className="absolute z-30 mt-1.5 max-h-64 w-full overflow-auto rounded-xl border border-white/12 bg-elevated/95 p-1 shadow-lift backdrop-blur-xl"
          >
            {suggestions.map((c, i) => (
              <li
                key={c.code}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={i === activeIndex}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setActiveIndex(i)}
                onClick={() => choose(c.code)}
                className={cn(
                  "flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm",
                  i === activeIndex ? "bg-flamingo/15 text-white" : "text-white/80",
                )}
              >
                <span className="truncate">{c.name}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </FieldFrame>
  );
}
